"use client";

import { useState, useRef, useCallback, useEffect } from "react";

// =============================================================================
// TYPES
// =============================================================================

export type BootPhase = "loader" | "void" | "ring";

export interface BootSequenceState {
  /** Current phase */
  phase: BootPhase;

  /** Progress within current phase (0-1) */
  phaseProgress: number;

  /** Playback state */
  isPlaying: boolean;

  /** True once RING emergence is complete (interactive) */
  isComplete: boolean;

  /** Elapsed time in current phase (ms) */
  phaseElapsed: number;

  /** Total elapsed time since boot started (ms) */
  totalElapsed: number;

  /** True when assets have finished loading (triggers loader bloop) */
  assetsLoaded: boolean;
}

export interface BootSequenceControls {
  /** Jump directly to RING settled state */
  skip: () => void;

  /** Pause/resume animation */
  togglePause: () => void;

  /** Signal that assets are loaded (triggers LOADER → VOID transition) */
  onAssetsLoaded: () => void;
}

export type UseBootSequenceReturn = BootSequenceState & BootSequenceControls;

// =============================================================================
// CONSTANTS
// =============================================================================

/** Phase durations in milliseconds */
const PHASE_DURATIONS: Record<BootPhase, number> = {
  loader: Infinity, // Waits for onAssetsLoaded() + bloop animation
  void: 1800, // 1.8s particle fade in
  ring: 3000, // 3s emergence animation - tightened from 5s
};

/** Phase order for transitions */
const PHASE_ORDER: BootPhase[] = ["loader", "void", "ring"];

/** Where skip() jumps to from each phase */
const SKIP_TARGETS: Record<BootPhase, BootPhase> = {
  loader: "ring",
  void: "ring",
  ring: "ring",
};

/** Delay after assets load before transitioning (bloop animation time) */
const LOADER_BLOOP_DELAY = 400;


// =============================================================================
// HOOK
// =============================================================================

export function useBootSequence({ passive = false }: { passive?: boolean } = {}): UseBootSequenceReturn {
  // ----- State -----
  const [phase, setPhase] = useState<BootPhase>("loader");
  const [phaseProgress, setPhaseProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [phaseElapsed, setPhaseElapsed] = useState(0);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [assetsLoaded, setAssetsLoaded] = useState(false);

  // ----- Refs (mutable, don't trigger re-renders) -----
  const phaseStartTimeRef = useRef<number>(performance.now());
  const bootStartTimeRef = useRef<number>(performance.now());
  const pausedAtRef = useRef<number | null>(null);
  const totalPausedTimeRef = useRef<number>(0);
  const assetsLoadedRef = useRef(false);
  const loaderBloopCompleteRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);
  const phaseRef = useRef<BootPhase>("loader");

  // Keep phaseRef in sync with phase state
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // ----- Transition to next phase -----
  const transitionTo = useCallback((nextPhase: BootPhase, skipEmergence = false) => {
    const now = performance.now();
    phaseStartTimeRef.current = now;
    phaseRef.current = nextPhase;
    setPhase(nextPhase);
    setPhaseProgress(skipEmergence ? 1 : 0);
    setPhaseElapsed(skipEmergence ? PHASE_DURATIONS.ring : 0);

    // Mark complete when ring emergence finishes (or immediately if skipped)
    if (nextPhase === "ring") {
      if (skipEmergence) {
        setIsComplete(true);
      } else {
        setTimeout(() => {
          setIsComplete(true);
        }, PHASE_DURATIONS.ring);
      }
    }
  }, []);

  // ----- Skip control -----
  const skip = useCallback(() => {
    const current = phaseRef.current;
    const target = SKIP_TARGETS[current];

    if (target !== current) {
      // Skip to ring with emergence complete
      transitionTo(target, true);
    } else if (current === "ring" && !isComplete) {
      // Already in ring but emergence still running - complete immediately
      setIsComplete(true);
      setPhaseProgress(1);
    }
  }, [transitionTo, isComplete]);

  // ----- Pause/Resume control -----
  const togglePause = useCallback(() => {
    setIsPlaying((prev) => {
      if (prev) {
        // Pausing: record current elapsed time
        const now = performance.now();
        pausedAtRef.current = now - phaseStartTimeRef.current;
      } else {
        // Resuming: adjust start time to maintain elapsed position
        const now = performance.now();
        const pauseDuration = pausedAtRef.current !== null
          ? now - phaseStartTimeRef.current - pausedAtRef.current
          : 0;
        totalPausedTimeRef.current += pauseDuration;
        phaseStartTimeRef.current = now - (pausedAtRef.current ?? 0);
        pausedAtRef.current = null;
      }
      return !prev;
    });
  }, []);

  // ----- Assets loaded signal -----
  const onAssetsLoaded = useCallback(() => {
    assetsLoadedRef.current = true;
    setAssetsLoaded(true);

    // Trigger bloop animation delay
    setTimeout(() => {
      loaderBloopCompleteRef.current = true;
    }, LOADER_BLOOP_DELAY);
  }, []);

  // ----- RAF tick loop -----
  const lastTotalElapsedUpdateRef = useRef(0);
  useEffect(() => {
    const tick = (now: number) => {
      // Calculate total elapsed (excluding paused time)
      const totalEl = now - bootStartTimeRef.current - totalPausedTimeRef.current;
      // Throttle totalElapsed updates to every ~100ms (debug display only)
      if (now - lastTotalElapsedUpdateRef.current > 100) {
        setTotalElapsed(totalEl);
        lastTotalElapsedUpdateRef.current = now;
      }

      // Skip tick if paused
      if (!isPlaying) {
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      const currentPhase = phaseRef.current;
      const elapsed = now - phaseStartTimeRef.current;

      // Special case: LOADER waits for assets + bloop
      if (currentPhase === "loader") {
        // Throttle loader updates too
        if (now - lastTotalElapsedUpdateRef.current > 33) {
          setPhaseElapsed(elapsed);
          lastTotalElapsedUpdateRef.current = now;
        }
        if (assetsLoadedRef.current && loaderBloopCompleteRef.current) {
          transitionTo("void");
        }
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      const duration = PHASE_DURATIONS[currentPhase];
      const progress = Math.min(1, elapsed / duration);

      // Throttle phase updates to ~30fps (every ~33ms) to reduce re-renders
      if (now - lastTotalElapsedUpdateRef.current > 33) {
        setPhaseElapsed(elapsed);
        setPhaseProgress(progress);
        lastTotalElapsedUpdateRef.current = now;
      }

      // Check for phase completion (auto-transition to next)
      if (elapsed >= duration) {
        const currentIdx = PHASE_ORDER.indexOf(currentPhase);
        if (currentIdx < PHASE_ORDER.length - 1) {
          transitionTo(PHASE_ORDER[currentIdx + 1]);
        }
        // RING phase: no next phase, just holds at progress=1
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    // Start the loop
    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [isPlaying, transitionTo]);

  // ----- Interaction-to-skip behavior -----
  // Any user interaction (scroll, touch, click, key) during boot skips ahead:
  // - During VOID: jump to RING
  // - During RING emergence: fast-forward to isComplete
  const skipInteractionRef = useRef(false); // Debounce so first interaction triggers skip once

  useEffect(() => {
    // Reset debounce when phase changes
    skipInteractionRef.current = false;
  }, [phase]);

  useEffect(() => {
    // Passive mode: DebugOverlay reads boot state for display only — no event listeners
    if (passive) return;

    const handleSkipInteraction = (e: Event) => {
      const current = phaseRef.current;

      // Already interactive — let events through normally
      if (current === "ring" && isComplete) return;

      // Loader phase — don't skip (bloop needs to finish)
      if (current === "loader") {
        // Still prevent scroll during loader
        if (e.type === "wheel") e.preventDefault();
        return;
      }

      // Prevent default scroll during boot
      if (e.type === "wheel" || e.type === "touchmove") {
        e.preventDefault();
      }

      // Debounce: one skip per phase
      if (skipInteractionRef.current) return;
      skipInteractionRef.current = true;

      // VOID → jump to ring
      // RING (emergence) → fast-forward to complete
      skip();
    };

    // Wheel: prevent scroll + skip
    window.addEventListener("wheel", handleSkipInteraction, { passive: false });
    // Touch: tap or swipe to skip
    window.addEventListener("touchstart", handleSkipInteraction, { passive: true });
    window.addEventListener("touchmove", handleSkipInteraction, { passive: false });
    // Click/tap (pointer unified)
    window.addEventListener("pointerdown", handleSkipInteraction, { passive: true });
    // Any key press
    window.addEventListener("keydown", handleSkipInteraction, { passive: true });

    return () => {
      window.removeEventListener("wheel", handleSkipInteraction);
      window.removeEventListener("touchstart", handleSkipInteraction);
      window.removeEventListener("touchmove", handleSkipInteraction);
      window.removeEventListener("pointerdown", handleSkipInteraction);
      window.removeEventListener("keydown", handleSkipInteraction);
    };
  }, [skip, isComplete, passive]);

  // ----- Return combined state + controls -----
  return {
    // State
    phase,
    phaseProgress,
    isPlaying,
    isComplete,
    phaseElapsed,
    totalElapsed,
    assetsLoaded,

    // Controls
    skip,
    togglePause,
    onAssetsLoaded,
  };
}
